import React, { forwardRef } from 'react';
import { Message, useFormContext, ValidationRule } from 'react-hook-form';

import InputField, { InputFieldProps } from '../InputField';

const MIN_AGE = 18;
const MIN_AGE_MESSAGE = `You must be at least ${MIN_AGE} years old`;

export type DateFieldProps = InputFieldProps & {
  name: string;
  placeholder: string;
  minAgeConfig?: {
    value: number;
    message: string;
  };
  maxAgeConfig?: {
    value: number;
    message: string;
  };
  requiredConfig?: Message | ValidationRule<boolean>;
};

function DateField(
  props: DateFieldProps,
  ref: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const {
    name,
    placeholder,
    minAgeConfig,
    maxAgeConfig,
    requiredConfig = 'Please enter a valid date',
    ...rest
  } = props;

  const { watch } = useFormContext();

  return (
    <InputField
      ref={ref}
      name={name}
      placeholder={placeholder}
      type="date"
      sx={{
        minWidth: '-webkit-fill-available',
        width: '100%',
        position: 'relative',
        '::-webkit-date-and-time-value': {
          textAlign: 'left',
          paddingLeft: 3,
          fontSize: 'inherit',
        },
        '&.empty': {
          ':not(:focus)::-webkit-datetime-edit': {
            color: 'transparent',
          },
          ':not(:focus):before': {
            color: 'tints.uiGrey',
            content: 'attr(placeholder)',
            fontSize: 'inherit',
            position: 'absolute',
            top: '25%',
            left: 4,
            display: 'block',
          },
        },
      }}
      className={watch(name, '').length > 0 ? '' : 'empty'}
      autoComplete="bday"
      validations={{
        required: requiredConfig,
        validate: (value) => {
          if (value) {
            const [year, month, day] = value.split('-');
            const dob = new Date(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
            );
            const ageDiffMs = Date.now() - dob.getTime();
            const ageDate = new Date(ageDiffMs);
            const age = Math.abs(ageDate.getUTCFullYear() - 1970);

            if (
              (minAgeConfig?.value && age < minAgeConfig.value) ||
              (age < MIN_AGE && !minAgeConfig?.value) ||
              ageDiffMs <= 0
            ) {
              return minAgeConfig?.message || MIN_AGE_MESSAGE;
            }

            if (maxAgeConfig && age >= maxAgeConfig.value) {
              return maxAgeConfig.message;
            }
          }
        },
      }}
      {...rest}
    />
  );
}

export default forwardRef(DateField);
